import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Menu, Phone, ChevronDown } from 'lucide-react';
import { LOGO_URL, SCHOOL } from '../lib/constants';
import { useLeadModal } from '../lib/leadModalContext';

/*
 * Nav items:
 *   - `to`     : router path (goes to a dedicated page)
 *   - `hash`   : scroll anchor on homepage (used when already on homepage)
 */
const NAV_LINKS = [
  { label: 'Adult Karate', to: '/adult-karate', hash: '#adult-karate' },
  { label: 'Classes', to: '/#programs', hash: '#programs' },
  { label: 'Schedule', to: '/schedule', hash: '#schedule' },
  { label: 'About', to: '/about', hash: '#about' },
  { label: 'FAQ', to: '/faq', hash: '#faq' },
  { label: 'Contact', to: '/contact', hash: '#contact' },
];

const PROGRAM_LINKS = [
  { label: 'Adult Karate', to: '/adult-karate' },
  { label: 'Kids Karate', to: '/kids-karate' },
  { label: 'Teen Martial Arts', to: '/teen-martial-arts' },
  { label: 'Kempo Karate', to: '/kempo-karate' },
  { label: 'Self-Defense', to: '/self-defense' },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const { openLeadModal } = useLeadModal();
  const location = useLocation();
  const onHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // When on homepage, prefer hash anchor (smooth scroll). Otherwise, go to deep page.
  const linkFor = (item) => (onHome && item.hash ? item.hash : item.to);
  const isAnchor = (href) => href && href.startsWith('#');

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-200 ${
        scrolled ? 'bg-black/70 backdrop-blur-md border-b border-white/10' : 'bg-transparent'
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        {/* Left: brand */}
        <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0" data-testid="header-logo">
          <div className="relative h-10 w-10 shrink-0 rounded-full overflow-hidden glow-red-sm">
            <img
              src={LOGO_URL}
              alt="Universal Kempo Karate"
              className="absolute inset-0 h-full w-full object-cover"
              width="40"
              height="40"
            />
          </div>
          <div className="flex flex-col leading-tight min-w-0">
            <span className="hidden sm:inline font-display text-[11px] tracking-[0.22em] uppercase text-white/70">
              Universal Kempo
            </span>
            <span className="font-display text-[13px] sm:text-sm tracking-[0.14em] uppercase text-white truncate">
              <span className="hidden sm:inline">Karate — </span>
              <span className="sm:hidden">Univ. Kempo · </span>
              Revolution
            </span>
          </div>
        </Link>

        {/* Center: Nav (desktop) */}
        <nav className="hidden lg:flex items-center gap-6" data-testid="header-nav">
          {NAV_LINKS.map((l) => {
            const href = linkFor(l);
            return isAnchor(href) ? (
              <a
                key={l.label}
                href={href}
                className="font-display text-[12px] uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.label}
                to={href}
                className="font-display text-[12px] uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {l.label}
              </Link>
            );
          })}
          {/* Programs dropdown for deep-page SEO linking */}
          <div
            className="relative"
            onMouseEnter={() => setProgramsOpen(true)}
            onMouseLeave={() => setProgramsOpen(false)}
          >
            <button
              type="button"
              className="inline-flex items-center gap-1 font-display text-[12px] uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors"
              aria-expanded={programsOpen}
              aria-haspopup="true"
              data-testid="nav-programs-dropdown"
              onClick={() => setProgramsOpen((v) => !v)}
            >
              Programs <ChevronDown className="h-3 w-3" />
            </button>
            {programsOpen && (
              <div className="absolute top-full left-0 mt-2 min-w-[220px] panel-solid rounded-xl p-2 border border-white/10 shadow-xl">
                {PROGRAM_LINKS.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    className="block px-3 py-2 rounded-lg font-display uppercase tracking-[0.14em] text-[11px] text-white/80 hover:bg-white/5 hover:text-white"
                    data-testid={`nav-program-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {p.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Right: CTAs */}
        <div className="hidden md:flex items-center gap-2">
          <a
            href={SCHOOL.phoneHref}
            className="group inline-flex items-center gap-2.5 rounded-xl px-4 py-2.5 bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] text-white transition-colors glow-red-md chat-orb-pulse"
            data-testid="header-call-text-link"
            aria-label={`Call now ${SCHOOL.phone}`}
          >
            <span className="grid place-items-center h-7 w-7 rounded-full bg-white/15 border border-white/25">
              <Phone className="h-3.5 w-3.5" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-display uppercase tracking-[0.18em] text-[10px] text-white/85">
                Call Now
              </span>
              <span className="font-display text-sm font-semibold tracking-tight">
                {SCHOOL.phone}
              </span>
            </span>
          </a>
          <Button
            onClick={() => openLeadModal('header')}
            className="font-display uppercase tracking-[0.14em] text-xs bg-white/5 hover:bg-white/10 text-white border border-white/15 rounded-xl px-4 py-2"
            data-testid="header-book-trial-button"
          >
            Book Free Trial
          </Button>
        </div>

        {/* Mobile menu */}
        <div className="md:hidden shrink-0">
          <Sheet>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                className="h-11 w-11 inline-flex items-center justify-center rounded-xl border border-white/15 text-white active:bg-white/5"
                data-testid="header-mobile-menu-button"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0B0D10] border-l border-white/10 text-white overflow-y-auto">
              <SheetTitle className="font-display uppercase tracking-[0.18em] text-sm text-white/80">
                Universal Kempo
              </SheetTitle>
              <nav className="mt-8 flex flex-col gap-1.5" aria-label="Mobile navigation">
                {NAV_LINKS.map((l) => {
                  const href = linkFor(l);
                  const Comp = isAnchor(href) ? 'a' : Link;
                  const linkProps = isAnchor(href) ? { href } : { to: href };
                  return (
                    <Comp
                      key={l.label}
                      {...linkProps}
                      className="font-display uppercase tracking-[0.16em] text-sm text-white/80 hover:text-white py-2.5 min-h-[44px] flex items-center"
                      data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {l.label}
                    </Comp>
                  );
                })}
                <div className="h-px bg-white/10 my-2" />
                <div className="font-display uppercase tracking-[0.18em] text-[10px] text-white/50 pt-1">
                  Programs
                </div>
                {PROGRAM_LINKS.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    className="font-display uppercase tracking-[0.14em] text-xs text-white/70 hover:text-white py-2 min-h-[40px] flex items-center"
                    data-testid={`mobile-nav-program-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {p.label}
                  </Link>
                ))}
                <div className="h-px bg-white/10 my-2" />
                <a
                  href={SCHOOL.phoneHref}
                  className="inline-flex items-center gap-2.5 rounded-xl px-4 py-3 min-h-[52px] bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] text-white glow-red-md"
                  data-testid="mobile-call-link"
                >
                  <Phone className="h-4 w-4" />
                  <span className="flex flex-col leading-tight">
                    <span className="font-display uppercase tracking-[0.18em] text-[10px] text-white/85">Call Now</span>
                    <span className="font-display text-sm font-semibold">{SCHOOL.phone}</span>
                  </span>
                </a>
                <Button
                  onClick={() => openLeadModal('mobile-header')}
                  className="mt-2 min-h-[48px] bg-white/5 hover:bg-white/10 text-white border border-white/15 font-display uppercase tracking-[0.14em]"
                  data-testid="mobile-book-trial-button"
                >
                  Book Free Trial
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
